import { useCallback, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  fetchAuditLogsPage,
  DEFAULT_AUDIT_LOGS_PAGE_SIZE,
  type AuditLogsFilters,
  type AuditLogsPage,
  type AuditLogsPageSize,
} from '../repositories/auditLogs.js';

/**
 * S-08: audit_logs 横断ビューの hook。
 *
 * - filters / pageSize 変更時は cursor stack をリセットして 1 ページ目から取得し直す
 * - cursor stack で「次/前」を表現:
 *   - 次ページ: 現在の page の `nextCursor` を stack に push
 *   - 前ページ: stack を pop（startAfter なしの初回ページに戻れる）
 * - cache key には filters + pageSize + 現在の cursor を含める。cursor は object 同一性で
 *   判定するため、stack の length を key に使う（cursor 自体は serializable でないため）
 */
export interface UseAuditLogsOptions {
  filters: AuditLogsFilters;
  pageSize: AuditLogsPageSize;
}

export interface UseAuditLogsResult {
  query: UseQueryResult<AuditLogsPage, Error>;
  /** 1-indexed の現在ページ番号 */
  pageIndex: number;
  /** 「次へ」可否。data.nextCursor が non-null */
  canGoNext: boolean;
  /** 「前へ」可否。pageIndex > 1 */
  canGoPrev: boolean;
  goNext: () => void;
  goPrev: () => void;
  /** filters / pageSize 変更時に呼んで cursor stack を初期化する */
  reset: () => void;
}

export function useAuditLogs({
  filters,
  pageSize,
}: UseAuditLogsOptions): UseAuditLogsResult {
  // 各ページの cursor stack。stack[0] = undefined (初回)、stack[i] = i ページ目の startAfter
  // pageIndex は stack.length (1-indexed)
  const [cursorStack, setCursorStack] = useState<
    Array<QueryDocumentSnapshot | null | undefined>
  >([undefined]);

  const currentCursor = cursorStack[cursorStack.length - 1];
  const pageIndex = cursorStack.length;

  // queryKey に cursor 自体は載せられない (object identity 比較できない) ため、
  // pageIndex を identifier として使う。filters / pageSize 変更時は別 key になる
  const query = useQuery<AuditLogsPage, Error>({
    queryKey: [
      'audit_logs',
      filters.tenantId ?? '',
      filters.action ?? '',
      filters.actorUid ?? '',
      filters.startAt ?? '',
      filters.endAt ?? '',
      pageSize,
      pageIndex,
    ],
    queryFn: () =>
      fetchAuditLogsPage({
        filters,
        pageSize,
        cursor: currentCursor ?? null,
      }),
    placeholderData: (prev) => prev,
  });

  const goNext = useCallback(() => {
    const nextCursor = query.data?.nextCursor;
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
  }, [query.data?.nextCursor]);

  const goPrev = useCallback(() => {
    setCursorStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const reset = useCallback(() => {
    setCursorStack([undefined]);
  }, []);

  return {
    query,
    pageIndex,
    canGoNext: !!query.data?.nextCursor,
    canGoPrev: pageIndex > 1,
    goNext,
    goPrev,
    reset,
  };
}

export const DEFAULT_PAGE_SIZE = DEFAULT_AUDIT_LOGS_PAGE_SIZE;
